/**
 * Format a timestamp to a readable date string
 */
export function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(timestamp))
}

/**
 * Format a timestamp to a readable date and time string
 */
export function formatDateTime(timestamp: number): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(timestamp))
}

/**
 * Format a timestamp to relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now()
  const diff = now - timestamp
  
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30)
  
  if (months > 0) return `${months} month${months > 1 ? 's' : ''} ago`
  if (weeks > 0) return `${weeks} week${weeks > 1 ? 's' : ''} ago`
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
  return 'Just now'
}

/**
 * Format a number as percentage
 */
export function formatPercentage(value: number, decimals: number = 0): string {
  return `${value.toFixed(decimals)}%`
}

/**
 * Format a number with thousand separators
 */
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value)
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 3) + '...'
}

/**
 * Capitalize first letter of each word
 */
export function titleCase(text: string): string {
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Convert snake_case or kebab-case to Title Case
 */
export function toReadableLabel(text: string): string {
  return text
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Format branch status to readable text
 */
export function formatBranchStatus(status: string): string {
  const statusMap: Record<string, string> = {
    active: 'Active',
    terminated_natural: 'Completed',
    terminated_manual: 'Terminated',
    pruned: 'Pruned',
  }
  return statusMap[status] || status
}

/**
 * Format conversation state to readable text
 */
export function formatConversationState(state: string): string {
  const stateMap: Record<string, string> = {
    IDLE: 'Ready to start',
    CLEANING: 'Processing input...',
    CONFIRM_CLEAN: 'Review cleaned input',
    QUESTIONING: 'Generating questions...',
    ANSWERING: 'Answering questions',
    AAI_ASSIST: 'Getting AI assistance...',
    EVALUATING: 'Evaluating responses...',
    READY_TO_GENERATE: 'Ready to generate problem',
    GENERATING: 'Generating problem statement...',
    REVIEWING: 'Review problem statement',
    REVISING: 'Revising...',
    EXPORTING: 'Exporting...',
    ERROR: 'Error occurred',
    COMPLETED: 'Completed',
  }
  return stateMap[state] || state
}

/**
 * Format file size in bytes to human readable
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

/**
 * Format duration in milliseconds to readable time
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}

/**
 * Get initials from a name
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')
}

/**
 * Format AI3C element name
 */
export function formatAI3CElement(element: string): string {
  const elementMap: Record<string, string> = {
    activity: 'Activity',
    item: 'Item',
    contradiction: 'Contradiction',
    constraint: 'Constraints',
    criteria: 'Criteria',
    cause: 'Cause',
    effect: 'Effect',
    undesirable: 'Undesirable Situation',
    desirable: 'Desired Situation',
  }
  return elementMap[element] || element
}
